import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  FormControlLabel,
  Checkbox,
  DialogActions,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import {
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";

import { Pagination } from "../../../../library/component";
import styles from "./CarouselListing.module.scss";
import { api } from "../../../../services";
import BannerItem from "./BannerItem";
import MultiInput from "../../../../library/common/MultiInput";
import { filterBanners } from "./filterBanners";

const pathOptions = ["home", "book", "article", "blog", "tirth"];

const CarouselListing = () => {
  const [carouselList, setCarouselList] = useState([]);
  const [totalBanners, setTotalBanners] = useState(0);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    pageNo: 0,
    pageSize: 10,
    search: "",
    sortBy: "",
    order: "asc",
  });
  const [modalFilters, setModalFilters] = useState({});
  const [modalSelectedValues, setModalSelectedValues] = useState({});
  const [isOpen, setIsOpen] = useState({ filters: false, sort: false });
  const anchorRef = useRef(null);
  const allBanners = useRef([]);

  // Helper to update filters and reset page number.
  const updateFilters = useCallback((newFilter, resetPage = true) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
      pageNo: resetPage ? 0 : newFilter.pageNo ?? prev.pageNo,
    }));
  }, []);

  // Handlers for main filters
  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFilters({ [name]: value });
    },
    [updateFilters]
  );

  // Modal filter change handler.
  const handleModalFilterChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    // If the field returns an array (from MultiInput), join the values.
    if (name === "path") {
      newValue = Array.isArray(newValue) ? newValue.join(",") : newValue;
    }
    setModalFilters((prev) => ({ ...prev, [name]: newValue }));
  }, []);

  // Modal and sort handlers.
  const openFilterModal = () => setIsOpen({ filters: true, sort: false });
  const openSortModal = () => setIsOpen({ filters: false, sort: true });
  const closeModal = () => setIsOpen({ filters: false, sort: false });

  // Toggle sorting for a given field.
  const handleSortToggle = (sortField) => {
    setFilters((prev) => {
      if (prev.sortBy !== sortField) {
        return { ...prev, sortBy: sortField, order: "asc", pageNo: 0 };
      } else {
        if (prev.order === "asc") {
          return { ...prev, order: "desc", pageNo: 0 };
        } else if (prev.order === "desc") {
          return { ...prev, sortBy: "", order: "asc", pageNo: 0 };
        }
      }
      return prev;
    });
  };

  // Apply modal filters to main filters
  const applyModalFilters = () => {
    updateFilters(modalFilters);
    closeModal();
  };

  const updateBannerList = useCallback(() => {
    filterBanners({
      setTotalBanners,
      setCarouselList,
      banners: allBanners.current,
      filters,
    });
  }, [filters]);

  const getBannerList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/banners");
      allBanners.current = response?.data?.banners || [];
      updateBannerList();
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false);
    }
  }, [updateBannerList]);

  useEffect(() => {
    getBannerList();
  }, [getBannerList]);

  useEffect(() => {
    updateBannerList();
  }, [updateBannerList]);

  // Pagination handlers.
  const handlePageChange = (event, newPage) => {
    updateFilters({ pageNo: newPage }, false);
  };

  const handleRowsPerPageChange = (event) => {
    updateFilters({ pageSize: parseInt(event.target.value, 10) });
  };

  return (
    <div className={styles.carouselListing}>
      <Paper className={styles.paper_container}>
        <Box className={styles.header_search_add_container}>
          <Typography className={styles.header_title} textAlign="center">
            Carousel Images
          </Typography>
        </Box>
        <Box
          className={styles.search_add_container}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <TextField
            className={styles.search_field}
            placeholder="Search Banners"
            variant="outlined"
            size="small"
            name="search"
            value={filters?.search}
            onChange={handleFilterChange}
            sx={{ minWidth: 800 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {/* <SearchBar
          placeholderText={SEARCH_BANNER_IMAGE_LABEL}
          updateSearchText={handleSearch}
        /> */}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            color="primary"
            variant="contained"
            size="medium"
            style={{ marginRight: "auto" }}
            onClick={() => navigate("/banner/add")}
          >
            ADD BANNER
          </Button>
          <Button ref={anchorRef} variant="outlined" onClick={openSortModal}>
            Sort By
          </Button>
          <Menu
            open={isOpen.sort}
            anchorEl={anchorRef.current}
            onClose={closeModal}
            disableAutoFocusItem
          >
            <MenuItem onClick={() => handleSortToggle("altText")}>
              <Box display="flex" alignItems="center" width="100%">
                <span>Alt Text</span>
                {filters?.sortBy === "altText" && (
                  <Box ml="auto">
                    {filters?.order === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                )}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleSortToggle("title.en")}>
              <Box display="flex" alignItems="center" width="100%">
                <span>Title</span>
                {filters?.sortBy === "title.en" && (
                  <Box ml="auto">
                    {filters?.order === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                )}
              </Box>
            </MenuItem>
          </Menu>
          <IconButton
            onClick={openFilterModal}
            className={styles.filterButton}
            color="primary"
          >
            <FilterList />
          </IconButton>
        </Box>

        {/* Filters Modal */}
        <Dialog open={isOpen.filters} onClose={closeModal} fullWidth>
          <DialogTitle>Filters</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <MultiInput
                options={pathOptions}
                initialValues={modalSelectedValues?.path || []}
                onChange={handleModalFilterChange}
                name="path"
                label="Path"
                type="array"
                setFinalValues={setModalSelectedValues}
                select
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="activeBanners"
                    checked={modalFilters?.activeBanners || false}
                    onChange={handleModalFilterChange}
                  />
                }
                label="Only Active Banners"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              onClick={applyModalFilters}
              variant="contained"
              color="primary"
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        <Box className={styles.tableContainer}>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell className={styles.tableCellImage}>Image</TableCell>
                  <TableCell>Alt Text</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell className={styles.tableCellActions}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <TableCell key={idx}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : carouselList?.length > 0 ? (
                  carouselList?.map((item, index) => (
                    <BannerItem
                      key={`banner-${index}`}
                      bannerItem={item}
                      getBannerList={getBannerList}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className={styles.no_data_cell}>
                      <Typography variant="h6" align="center">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          totalCount={totalBanners}
          pageSize={filters?.pageSize}
          pageNo={filters?.pageNo}
          pageChangeHandler={handlePageChange}
          rowsPerPageHandler={handleRowsPerPageChange}
        />
      </Paper>
    </div>
  );
};

export default CarouselListing;
